import { useEffect } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { toast } from 'sonner';
import { notify } from '../../toaster';
import { type ChatStore, chatStore, isStreamingStatus } from './chatStore';

const TOAST_ID = 'chat-agent-update';

const openChat = (chatId: string) => {
  const state = chatStore.getState();
  const client = state.clients[chatId];
  if (client) state.setDrawerClient(client);
  state.setIsOpen(true);
};

/**
 * A chat store listener. When a drawer chat's run ends, whatever the outcome, while the drawer is
 * closed, it toasts with an action that opens that chat. One toast at a time: a later run replaces
 * it. Opening the drawer dismisses it.
 */
export const createAgentUpdateListener =
  (intl: IntlShape) => (state: ChatStore, prevState: ChatStore) => {
    if (state.isOpen && !prevState.isOpen) toast.dismiss(TOAST_ID);
    if (state.isOpen || state.sessions === prevState.sessions) return;

    const endedChatId = Object.keys(state.seenRunEnd).findLast(
      chatId =>
        isStreamingStatus(prevState.sessions[chatId]?.status) &&
        !isStreamingStatus(state.sessions[chatId]?.status)
    );
    if (!endedChatId) return;

    notify.default({
      id: TOAST_ID,
      title: intl.formatMessage({ id: 'features.chat.notification.update' }),
      toastActions: [
        {
          label: intl.formatMessage({
            id: 'features.chat.notification.openChat',
          }),
          onClick: () => openChat(endedChatId),
        },
      ],
    });
  };

export const useAgentUpdateToast = () => {
  const intl = useIntl();

  // A store subscription, not derived state: the drawer may not be mounted when the run ends.
  useEffect(() => chatStore.subscribe(createAgentUpdateListener(intl)), [intl]);
};
